import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Activity, Dumbbell, Trophy, ArrowUpRight } from 'lucide-react';
import logo from '../assets/logo.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faAndroid } from '@fortawesome/free-brands-svg-icons';
import './PublicUserProfile.css';
import PublicProfileHighlights from './PublicProfileHighlights';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const APP_DOWNLOAD_URL = 'https://linktr.ee/snowsproam';

function AthleteSportIcon({ sport }) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const iconUrl = sport.sports?.icon_url;

  return (
    <span className="athlete-sport-icon" aria-hidden="true">
      {iconUrl && !imageFailed
        ? <img src={iconUrl} alt="" loading="lazy" onError={() => setImageFailed(true)} />
        : <Activity size={20} />}
    </span>
  );
}

export default function PublicUserProfile() {
  const { username = '' } = useParams();
  const identifier = username.trim().replace(/^@/, '');
  const [profile, setProfile] = React.useState(null);
  const [sports, setSports] = React.useState([]);
  const [sportsError, setSportsError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const [imageFailed, setImageFailed] = React.useState(false);
  const appFallbackTimer = React.useRef(null);
  const appFallbackCleanup = React.useRef(null);
  React.useEffect(() => {
    let mounted = true;
    setLoading(true); setError(''); setProfile(null); setImageFailed(false); setSports([]); setSportsError(false);
    const load = async () => {
      try {
        if (!identifier || identifier.length > 48 || /[/%\\]/.test(identifier)) {
          if (mounted) setError('This profile link is invalid. Ask the athlete for their latest link.');
          return;
        }
        const query = supabase.from('profiles').select('id,username,name,surname,profile_img,current_country,country,level,bio,created_at');
        const { data, error: queryError } = await (UUID_PATTERN.test(identifier)
          ? query.eq('id', identifier)
          : query.ilike('username', identifier.replace(/_/g, '\\_'))).maybeSingle();
        if (queryError) throw queryError;
        if (mounted) {
          if (data) {
            setProfile(data);
            setLoading(false);
            const { data: sportRows, error: sportError } = await supabase.from('sport_profiles')
              .select('sport_id,club,position,sports!inner(id,active,icon_url)')
              .eq('profile_id', data.id).eq('active', true).eq('sports.active', true)
              .order('sport_id');
            if (mounted) {
              setSports(sportRows || []);
              setSportsError(Boolean(sportError));
            }
          }
          else setError('This athlete could not be found. Their username may have changed.');
        }
      } catch {
        if (mounted) setError('We could not load this profile. Check your connection and try again.');
      } finally { if (mounted) setLoading(false); }
    };
    load();
    return () => { mounted = false; };
  }, [identifier, attempt]);
  const name = [profile?.name, profile?.surname].filter(Boolean).join(' ') || profile?.username || 'Athlete';
  React.useEffect(() => {
    const previous = document.title;
    document.title = profile ? `${name} (@${profile.username}) · Snows ProAm` : 'Athlete profile · Snows ProAm';
    return () => { document.title = previous; };
  }, [profile, name]);
  const appLink = profile ? `snowsproam://profile/${encodeURIComponent(profile.id)}` : '';
  React.useEffect(() => () => {
    window.clearTimeout(appFallbackTimer.current);
    appFallbackCleanup.current?.();
  }, []);
  const openAthleteInApp = React.useCallback(event => {
    event.preventDefault();
    if (!appLink) return;
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobileDevice) {
      window.location.assign(APP_DOWNLOAD_URL);
      return;
    }
    window.clearTimeout(appFallbackTimer.current);
    appFallbackCleanup.current?.();
    let leftBrowser = false;
    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', cancelFallback);
      window.removeEventListener('blur', cancelFallback);
      appFallbackCleanup.current = null;
    };
    const cancelFallback = () => {
      leftBrowser = true;
      window.clearTimeout(appFallbackTimer.current);
      cleanup();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') cancelFallback();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', cancelFallback, { once: true });
    window.addEventListener('blur', cancelFallback, { once: true });
    appFallbackCleanup.current = cleanup;
    appFallbackTimer.current = window.setTimeout(() => {
      cleanup();
      if (!leftBrowser && document.visibilityState === 'visible') window.location.assign(APP_DOWNLOAD_URL);
    }, 2800);
    window.location.assign(appLink);
  }, [appLink]);
  return (
    <main className="athlete-page">
      <svg className="athlete-track-art" viewBox="0 0 1200 1200" fill="none" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map(i => <rect key={i} x={180 + i * 32} y={-160 + i * 32} width={820 - i * 64} height={1340 - i * 64} rx={410 - i * 32} stroke="currentColor" strokeWidth="3" />)}
        <path d="M80 520h160l65-110 80 220 75-160h120" stroke="currentColor" strokeWidth="7" />
      </svg>
      <header className="athlete-nav">
        <Link to="/" aria-label="Snows ProAm home"><img src={logo} alt="Snows ProAm" /></Link>
        <span>WHERE ATHLETES BELONG</span>
        <Link to="/sessions">Explore sessions <span aria-hidden="true">↗</span></Link>
      </header>
      <div className="athlete-content">
        <div className="athlete-eyebrow athlete-mobile-tagline"><span /> WHERE ATHLETES BELONG</div>
        <div className="athlete-intro"><div><div className="athlete-wordmark">SNOWS <span>PROAM</span></div><h1>Become your best.<br /><em>One session at a time.</em></h1></div><div className="athlete-sport-motif" aria-hidden="true"><Activity /><Dumbbell /><Trophy /><span>TRAIN / COMPETE / CONNECT</span></div></div>
        {loading ? <div className="athlete-state" role="status">Loading athlete profile…</div> : error ? (
          <section className="athlete-state" role="alert"><h2>Profile unavailable</h2><p>{error}</p><button onClick={() => setAttempt(n => n + 1)}>Try again</button><Link to="/">Back to Snows ProAm</Link></section>
        ) : (
          <section className="athlete-card" aria-label={`${name}'s profile`}>
            <div className="athlete-card-banner"><span>SNOWS <b>PROAM</b></span><div className="athlete-banner-icons" aria-hidden="true"><Activity /><Dumbbell /><Trophy /></div><span>ATHLETE PROFILE</span></div>
            <div className="athlete-card-body">
              <div className="athlete-identity">
                <div className="athlete-avatar">{profile.profile_img && !imageFailed ? <img src={profile.profile_img} alt={name} onError={() => setImageFailed(true)} /> : <span>{name.slice(0, 1).toUpperCase()}</span>}</div>
                <div className="athlete-name"><div className="athlete-eyebrow">PART OF THE PROAM COMMUNITY</div><h2>{name}</h2><p>@{profile.username}</p></div>
              </div>
              <section className="athlete-overview" aria-label="Profile overview">
                <h3>Overview</h3>
                {profile.bio ? <p className="athlete-bio">{profile.bio}</p> : <p>Part of the Snows ProAm community. Connecting through sport, fitness and a shared drive to improve.</p>}
                <p className="athlete-platform-copy">A place to train together, host sports sessions, join events and tournaments, and connect with other athletes.</p>
                {profile.created_at && !Number.isNaN(Date.parse(profile.created_at)) && <span className="athlete-member-since">ON PROAM SINCE {new Date(profile.created_at).getUTCFullYear()}</span>}
              </section>
              <div className="athlete-details"><div><span>BASED IN</span><strong>{profile.current_country || profile.country || 'Not shared'}</strong></div><div><span>PROAM LEVEL</span><strong className="athlete-level">{profile.level ?? 1}<small> / KEEP PROGRESSING</small></strong></div></div>
              {sports.length > 0 && <section className="athlete-sports"><div className="athlete-section-heading"><h3>My sports</h3><span>THE WAY I MOVE</span></div><div className="athlete-sports-grid">{sports.map(sport => <div className="athlete-sport" key={sport.sport_id}><AthleteSportIcon sport={sport} /><div><strong>{sport.sport_id}</strong>{(sport.club || sport.position) && <p>{[sport.club, sport.position].filter(Boolean).join(' · ')}</p>}</div></div>)}</div></section>}
              {sportsError && <p className="athlete-sports-error" role="status">Sports could not be loaded. <button onClick={() => setAttempt(n => n + 1)}>Try again</button></p>}
              <PublicProfileHighlights key={profile.id} profile={profile} appLink={appLink} onOpenApp={openAthleteInApp} />
              <div className="athlete-connect"><div><h3>Better together.</h3><p>Connect with @{profile.username} in Snows ProAm.<br />Open their profile and tap the Add Friend icon to send them a friend request.</p></div><a className="athlete-primary" href={appLink} onClick={openAthleteInApp}>Open athlete in app <ArrowUpRight size={19} aria-hidden="true" /></a></div>
              <div className="athlete-download" aria-label="Download Snows ProAm">
                <div className="athlete-store-links">
                  <a href="https://apps.apple.com/ie/app/snows-proam/id6504996530"><FontAwesomeIcon icon={faApple} aria-hidden="true" /><span><small>Download for</small>iPhone</span></a>
                  <a href="https://play.google.com/store/apps/details?id=com.snowsproam.snowsproam"><FontAwesomeIcon icon={faAndroid} aria-hidden="true" /><span><small>Download for</small>Android</span></a>
                </div>
              </div>
            </div>
          </section>
        )}
        <nav className="athlete-explore-links" aria-label="Explore the Snows ProAm community">
          <a href="https://whereathletesbelong.com" target="_blank" rel="noopener noreferrer" aria-label="Where Athletes Belong (opens in a new tab)"><span>OUR STORY<strong>Where Athletes Belong</strong></span><ArrowUpRight size={20} aria-hidden="true" /></a>
          <a href="https://worldwidesteps.com" target="_blank" rel="noopener noreferrer" aria-label="Worldwide Steps (opens in a new tab)"><span>EVERY STEP COUNTS<strong>Worldwide Steps</strong></span><ArrowUpRight size={20} aria-hidden="true" /></a>
        </nav>
        <footer className="athlete-footer"><strong>EVERY ATHLETE. EVERY LEVEL.</strong><p>Train. Compete. Connect.</p><Link to="/">Discover Snows ProAm ↗</Link></footer>
      </div>
    </main>
  );
}
